import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ModusCheckboxModule, ModusIconModule } from '@trimble-gcs/modus';
import { FileSizePipe } from '@trimble-gcs/ngx-common';
import { TOGGLE_SELECTED } from '../../../../utils/toggle-selected';
import { ClarityTreeItem, ClarityTreeItemType } from '../clarity.models';
import { clarityFileBrowserTableTranslations } from './clarity-file-browser-table.translations';

@UntilDestroy()
@Component({
  selector: 'sd-clarity-file-browser-table',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusCheckboxModule,
    ModusIconModule,
    MatTableModule,
    FileSizePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './clarity-file-browser-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClarityFileBrowserTableComponent {
  private readonly toggleSelected = inject(TOGGLE_SELECTED);

  readonly translations = clarityFileBrowserTableTranslations();

  readonly treeItems = input.required<ClarityTreeItem[]>();
  readonly lastSelected = input<ClarityTreeItem | undefined>();
  readonly showPrevious = input(false);
  readonly disabled = input<boolean>(false);

  readonly onPreviousClick = output();
  readonly onFolderClick = output<ClarityTreeItem>();
  readonly onSelectionChange = output<ClarityTreeItem[]>();

  private readonly data = computed(() => {
    const sortedTreeItems = this.treeItems().sort(
      (a, b) => b.type.localeCompare(a.type) || a.name.localeCompare(b.name),
    );
    return this.showPrevious() ? [this.backTreeItem, ...sortedTreeItems] : sortedTreeItems;
  });

  readonly dataSource = computed(() => new MatTableDataSource<ClarityTreeItem>(this.data()));

  private readonly isAllSelected = computed(() => {
    const treeItems = this.treeItems().filter((item) => item.type === ClarityTreeItemType.file);
    return treeItems.length > 0 && treeItems.every((item) => item.selected);
  });

  readonly selectAllControl = new FormControl(false, { nonNullable: true });
  readonly treeItemType = ClarityTreeItemType;
  readonly displayedColumns = ['selected', 'thumbnailUrl', 'name', 'size'];

  private readonly backTreeItem: ClarityTreeItem = {
    id: '',
    type: ClarityTreeItemType.previous,
    name: '...',
    size: 0,
    selected: false,
    worldId: 0,
  };

  constructor() {
    this.createSelectAllEffect();
  }

  getItemId(index: number, item: ClarityTreeItem) {
    return item.id;
  }

  itemClick(item: ClarityTreeItem, mouseEvent: MouseEvent) {
    if (this.disabled()) return;

    if (item.type === ClarityTreeItemType.previous) {
      this.onPreviousClick.emit();
    } else if (item.type === ClarityTreeItemType.folder) {
      this.onFolderClick.emit(item);
    } else {
      const files = this.data().filter((item) => item.type === ClarityTreeItemType.file);
      const items = this.toggleSelected(files, item, mouseEvent, this.lastSelected());
      this.onSelectionChange.emit(items);
    }
  }

  toggleAllRows() {
    const selected = !this.isAllSelected();

    const items = this.treeItems()
      .filter((item) => item.type === ClarityTreeItemType.file)
      .map((item) => ({ ...item, selected }));

    this.onSelectionChange.emit(items);
  }

  checkboxKeyDown(treeItem: ClarityTreeItem) {
    treeItem.selected = !treeItem.selected;
    this.onSelectionChange.emit([treeItem]);
  }

  createSelectAllEffect() {
    effect(() => {
      this.selectAllControl.setValue(this.isAllSelected());
      if (this.disabled()) {
        this.selectAllControl.disable();
      } else {
        this.selectAllControl.enable();
      }
    });
  }
}
